import { useEffect, useId, useState } from 'react'
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react'
import { peticionDeAccesoDePlataforma } from '../../autenticacionPlataforma'
import {
  apiPlataforma,
  type DespachoDadoDeAlta,
  type DespachoDePlataforma,
  type MetricasDePlataforma,
  type PlanSuscripcion,
  type ResumenDeClienteDePlataforma,
} from '../../servicios/apiPlataforma'
import { ErrorDeApi } from '../../servicios/api'
import { explicarInvitacion } from '../../componentes/explicarInvitacion'
import { LogoDeMicrosoft } from '../../iconos'
import { useTrampaDeFoco } from '../../ganchos/useTrampaDeFoco'
import { telon } from '../../ganchos/telon'
import { CatalogosDePlataforma } from './CatalogosDePlataforma'

const PLANES: PlanSuscripcion[] = ['Gratuito', 'Pro', 'Despacho']

const numero = (n: number) => n.toLocaleString('es-MX')

/**
 * Tras este rato sin respuesta la consola explica la espera. En dev la primera consulta después
 * de un rato sin uso tarda hasta un minuto —la API arranca en frío y la SQL serverless sale de su
 * pausa—, y con «Cargando…» a secas la pantalla parecía rota (17/10/2026).
 */
export const ESPERA_LARGA_MS = 5_000

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

/**
 * X5 · El prototipo pide los estados «Límite cerca» y «Alta restringida». No son campos
 * nuevos: se derivan de lo que el DTO ya trae. Ojo, el prototipo también dibuja un «20 / ∞»
 * que aquí no aplica — los tres planes del dominio tienen tope finito (Gratuito 3, Pro 25,
 * Despacho 250 clientes), así que no hay estado ilimitado que pintar.
 */
export function consumoDelPlan(d: { clientesActivos: number; maxClientes: number; usuariosActivos: number; maxUsuarios: number }) {
  const proporcion = Math.max(
    d.maxClientes > 0 ? d.clientesActivos / d.maxClientes : 0,
    d.maxUsuarios > 0 ? d.usuariosActivos / d.maxUsuarios : 0,
  )
  if (proporcion >= 1) return { texto: 'Alta restringida', grado: 'vencido' as const }
  if (proporcion >= 0.9) return { texto: 'Límite cerca', grado: 'aviso' as const }
  return null
}

/**
 * Consola interna de administración de la plataforma (RF-107): personal propio de
 * ContaQuick, nunca un despacho. Ruta aparte (`/plataforma`), con su propia sesión de MSAL
 * (`autenticacionPlataforma`) — ver `main.tsx`, donde se monta con un `MsalProvider`
 * independiente del que usa el resto de la SPA.
 */
export function ConsolaDePlataforma() {
  return (
    <>
      <UnauthenticatedTemplate>
        <PortadaDePlataforma />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <ConsolaAutenticada />
      </AuthenticatedTemplate>
    </>
  )
}

function PortadaDePlataforma() {
  const { instance } = useMsal()
  const [redirigiendo, setRedirigiendo] = useState(false)

  return (
    <main className="portada">
      <div className="portada-tarjeta">
        <div className="logo">
          <span className="logo-cuadro" />
          <span className="logo-nombre">ContaQuick · Plataforma</span>
        </div>
        <div>
          <h1>Consola interna</h1>
          <p className="lema">Solo personal de ContaQuick. Esta sesión es independiente de la de los despachos.</p>
        </div>
        {/* X9 · «Ninguna; aplicar L3», dice el hallazgo, y tenía razón: el mismo botón mudo
            que había en la portada de los despachos estaba aquí. Se le aplica también L6,
            porque el defecto es el mismo y el remedio también.

            Dice «Microsoft» a secas, sin «Entra ID»: esta consola autentica contra un tenant
            NORMAL de fuerza de trabajo, no contra el CIAM de los despachos, y con
            `/common` sirve cualquier cuenta de trabajo o escuela. Ponerle el nombre del
            producto del otro lado sería preciso a medias. */}
        <button
          className="primario"
          disabled={redirigiendo}
          onClick={() => {
            setRedirigiendo(true)
            instance.loginRedirect(peticionDeAccesoDePlataforma).catch(() => setRedirigiendo(false))
          }}
        >
          <LogoDeMicrosoft />
          {redirigiendo ? 'Redirigiendo a Microsoft…' : 'Continuar con Microsoft'}
        </button>
      </div>
    </main>
  )
}

function ConsolaAutenticada() {
  const { instance } = useMsal()
  const cuenta = instance.getActiveAccount()
  // null = todavía no llega la lista. Con [] de entrada, mientras cargaba la tabla decía «Aún no
  // hay despachos dados de alta»: un cero sin explicación, que es justo lo que no se vale.
  const [despachos, setDespachos] = useState<DespachoDePlataforma[] | null>(null)
  const [esperaLarga, setEsperaLarga] = useState(false)
  const [metricas, setMetricas] = useState<MetricasDePlataforma | null>(null)
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState<string | null>(null)
  const [verResumenDe, setVerResumenDe] = useState<DespachoDePlataforma | null>(null)
  const [dandoDeAlta, setDandoDeAlta] = useState(false)

  async function recargar() {
    try {
      setDespachos(await apiPlataforma.listarDespachos())
      setError('')
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar la lista de despachos.')
    }
    // Los indicadores son accesorios: si fallan, la consola sigue sirviendo para operar.
    await apiPlataforma.metricas().then(setMetricas).catch(() => setMetricas(null))
  }

  useEffect(() => {
    void recargar()
    const reloj = setTimeout(() => setEsperaLarga(true), ESPERA_LARGA_MS)
    return () => clearTimeout(reloj)
  }, [])

  async function cambiarPlan(despacho: DespachoDePlataforma, plan: PlanSuscripcion) {
    setError('')
    setOcupado(despacho.id)
    try {
      await apiPlataforma.cambiarPlan(despacho.id, plan)
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cambiar el plan.')
    } finally {
      setOcupado(null)
    }
  }

  async function alternarSuspension(despacho: DespachoDePlataforma) {
    setError('')
    setOcupado(despacho.id)
    try {
      if (despacho.estado === 'Suspendido') await apiPlataforma.reactivar(despacho.id)
      else await apiPlataforma.suspender(despacho.id)
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cambiar el estado del despacho.')
    } finally {
      setOcupado(null)
    }
  }

  return (
    <>
      {/* X2 · La consola interna se veía igual que la aplicación de un despacho, y desde aquí
          se suspenden tenants: la barra oscura es lo que distingue de un vistazo dónde está
          uno parado. X8 · Convivían tres nombres —«ContaQuick · Plataforma», «Consola
          interna» y «Consola de plataforma»—; queda uno. */}
      <div className="barra">
        <span className="marca">ContaQuick</span>
        <span>Consola interna de plataforma</span>
        <span className="espaciador" />
        <span className="usuario">{cuenta?.username}</span>
        <button onClick={() => instance.logoutRedirect()}>Salir</button>
      </div>

      <main id="contenido-principal" className="contenido">
      <header className="encabezado">
        <h1>Despachos</h1>
        <span className="encabezado-acciones">
          <button className="primario" onClick={() => setDandoDeAlta(true)}>Dar de alta despacho</button>
        </span>
      </header>

      {/* X3 · Este texto solo se veía dentro del modal de resumen contable, o sea justo cuando
          ya se está mirando contabilidad. Informa antes de actuar, no después. */}
      <p className="banda-rol">
        La consola gestiona métricas de uso, planes y suspensiones <strong>sin acceso al
        contenido contable</strong> de ningún despacho, salvo consentimiento explícito y
        vigente, que queda en la bitácora del propio despacho (RF-107 §6.4).
      </p>

      {error && <p className="error" role="alert">{error}</p>}

      {/* X1 · La consola no tenía un solo indicador: había que contar filas para saber cuántos
          despachos hay. Los cinco salen de una llamada. Los suspendidos van junto a los activos
          porque un cero ahí también es información: distingue «ninguno» de «no se revisó». */}
      {metricas && (
        <div className="tarjetas">
          <div className="tarjeta">
            <h2>Despachos activos</h2>
            <p className="cifra">{numero(metricas.despachosActivos)}</p>
            <p className="sutil">{numero(metricas.despachosSuspendidos)} suspendidos</p>
          </div>
          <div className="tarjeta">
            <h2>Contribuyentes</h2>
            <p className="cifra">{numero(metricas.contribuyentes)}</p>
            <p className="sutil">RFC activos en toda la plataforma</p>
          </div>
          <div className="tarjeta">
            <h2>CFDI procesados</h2>
            <p className="cifra">{numero(metricas.cfdiDelMes)}</p>
            <p className="sutil">{MESES[metricas.mes - 1]} {metricas.anio}</p>
          </div>
          <div className="tarjeta">
            <h2>Solicitudes SAT en cola</h2>
            <p className="cifra">{numero(metricas.solicitudesSatEnCola)}</p>
            <p className="sutil">esperando respuesta del SAT</p>
          </div>
        </div>
      )}

      <div className="tabla-envoltura">
        <table className="tabla">
          <thead>
            <tr>
              <th>Despacho</th>
              <th>Plan</th>
              <th>Estado</th>
              <th className="cupo">Clientes</th>
              <th className="cupo">Usuarios</th>
              <th className="cupo">CFDI · mes</th>
              {/* «Alta» a secas chocaba con el chip «Alta restringida» de la misma fila: dos
                  sentidos distintos de la palabra a dos columnas de distancia. */}
              <th>Desde</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(despachos ?? []).map((d) => (
              <tr key={d.id}>
                <td>{d.nombreComercial}</td>
                <td>
                  <select
                    value={d.plan}
                    disabled={ocupado === d.id}
                    onChange={(e) => void cambiarPlan(d, e.target.value as PlanSuscripcion)}
                    aria-label={`Plan de ${d.nombreComercial}`}
                  >
                    {PLANES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </td>
                <td>
                  {/* X5 · Con el cupo a la vista, «Activo» solo ya no basta: un despacho al
                      tope sigue activo pero no puede dar de alta nada más. */}
                  <span className={`insignia ${d.estado === 'Suspendido' ? 'vencido' : 'vigente'}`}>{d.estado}</span>
                  {d.estado !== 'Suspendido' && consumoDelPlan(d) && (
                    <>
                      {' '}
                      <span className={`insignia ${consumoDelPlan(d)!.grado}`}>{consumoDelPlan(d)!.texto}</span>
                    </>
                  )}
                </td>
                {/* X4 · Un conteo sin su tope no dice si queda espacio, que es lo único que se
                    decide desde aquí. Los tres planes tienen límite finito: no hay «∞». */}
                <td className="cupo">{numero(d.clientesActivos)} / {numero(d.maxClientes)}</td>
                <td className="cupo">{numero(d.usuariosActivos)} / {numero(d.maxUsuarios)}</td>
                <td className="cupo">{numero(d.cfdiDelMes)} / {numero(d.maxCfdiPorMes)}</td>
                <td className="sutil">{new Date(d.creadoEnUtc).toLocaleDateString('es-MX')}</td>
                <td>
                  <span className="acciones-de-fila">
                    <button className="enlace" disabled={ocupado === d.id} onClick={() => void alternarSuspension(d)}>
                      {d.estado === 'Suspendido' ? 'Reactivar' : 'Suspender'}
                    </button>
                    <button className="enlace" onClick={() => setVerResumenDe(d)}>Resumen contable</button>
                  </span>
                </td>
              </tr>
            ))}
            {despachos === null && (
              <tr>
                <td colSpan={8} className="vacio" role="status">
                  {error
                    ? 'La lista no se pudo cargar: el motivo está en el aviso de arriba.'
                    : esperaLarga
                      ? 'Sigue cargando. La primera consulta después de un rato sin uso puede tardar hasta un minuto mientras el servidor arranca.'
                      : 'Cargando despachos…'}
                </td>
              </tr>
            )}
            {despachos?.length === 0 && (
              <tr>
                <td colSpan={8} className="vacio">Aún no hay despachos dados de alta.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CatalogosDePlataforma />

      {verResumenDe && (
        <ResumenContableModal despacho={verResumenDe} onCerrar={() => setVerResumenDe(null)} />
      )}

      {dandoDeAlta && (
        <AltaDeDespachoModal
          onCerrar={() => setDandoDeAlta(false)}
          onCreado={() => {
            setDandoDeAlta(false)
            void recargar()
          }}
        />
      )}
      </main>
    </>
  )
}

/**
 * Alta de un despacho nuevo (RF-101, RF-107): único punto de alta ahora que el registro
 * público se cerró. Crea el despacho y a su administrador invitado; el administrador activa
 * su acceso al iniciar sesión con el mismo correo, igual que una invitación de colaborador.
 */
function AltaDeDespachoModal({ onCerrar, onCreado }: { onCerrar: () => void; onCreado: () => void }) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, onCerrar)
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  // El servidor guarda el correo del administrador en minúsculas y sin espacios: el aviso dice ese.
  const [listo, setListo] = useState<{ correo: string; resultado: DespachoDadoDeAlta } | null>(null)

  async function darDeAlta(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const datos = new FormData(evento.currentTarget)
    setError('')
    setEnviando(true)

    const rfcAdministrador = String(datos.get('rfcAdministrador') ?? '').trim()
    const telefonoAdministrador = String(datos.get('telefonoAdministrador') ?? '').trim()
    const correoContacto = String(datos.get('correoContacto'))

    try {
      const creado = await apiPlataforma.altaDeDespacho({
        nombreComercial: String(datos.get('nombreComercial')),
        correoContacto,
        nombreAdministrador: String(datos.get('nombreAdministrador')),
        ...(rfcAdministrador && { rfcAdministrador }),
        ...(telefonoAdministrador && { telefonoAdministrador }),
      })
      setListo({ correo: correoContacto.trim().toLowerCase(), resultado: creado })
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo dar de alta el despacho.')
    } finally {
      setEnviando(false)
    }
  }

  if (listo) {
    // #149 · Mismo resultado que una invitación: la cuenta del administrador se crea en el CIAM.
    const { aviso, pendiente } = explicarInvitacion(listo.correo, listo.resultado)
    return (
      <div className="modal" {...telon(() => onCreado())}>
        <div role="dialog" aria-modal="true" aria-labelledby={idTitulo} className="panel" onClick={(e) => e.stopPropagation()}>
          <h2 id={idTitulo}>Despacho dado de alta</h2>
          {aviso && <p className="sutil">{aviso}</p>}
          {pendiente && <p className="aviso-critico" role="status">{pendiente}</p>}
          <div className="acciones">
            <button className="primario" onClick={onCreado}>Entendido</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="modal" {...telon(() => onCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={darDeAlta}
      >
        <h2 id={idTitulo}>Dar de alta despacho</h2>

        <label>
          Nombre comercial del despacho
          <input name="nombreComercial" required maxLength={200} placeholder="Despacho Contable Ejemplo" />
        </label>

        <label>
          Correo del administrador
          <input name="correoContacto" type="email" required maxLength={320} placeholder="admin@despacho.mx" />
        </label>
        <p className="sutil">El administrador activará su acceso al iniciar sesión con este mismo correo.</p>

        <label>
          Nombre del administrador
          <input name="nombreAdministrador" required maxLength={200} placeholder="Nombre y apellidos" />
        </label>

        <label>
          RFC del administrador (opcional)
          <input name="rfcAdministrador" maxLength={13} placeholder="XAXX010101000" />
        </label>

        <label>
          Teléfono del administrador (opcional)
          <input name="telefonoAdministrador" type="tel" maxLength={20} placeholder="55 1234 5678" />
        </label>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={onCerrar}>Cancelar</button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Dando de alta…' : 'Dar de alta'}
          </button>
        </div>
      </form>
    </div>
  )
}

/**
 * Resumen contable de solo lectura (RF-107, §6.4): solo funciona si el despacho otorgó un
 * consentimiento de soporte vigente. Sin eso, el API responde 403 con
 * SIN_CONSENTIMIENTO_DE_SOPORTE y aquí se explica en vez de tratarse como un error genérico.
 */
function ResumenContableModal({ despacho, onCerrar }: { despacho: DespachoDePlataforma; onCerrar: () => void }) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLDivElement>(true, onCerrar)
  const [estado, setEstado] = useState<'cargando' | 'ok' | 'sinConsentimiento' | 'error'>('cargando')
  const [resumen, setResumen] = useState<ResumenDeClienteDePlataforma[]>([])
  const [mensaje, setMensaje] = useState('')

  useEffect(() => {
    apiPlataforma
      .resumenContable(despacho.id)
      .then((r) => {
        setResumen(r)
        setEstado('ok')
      })
      .catch((e) => {
        if (e instanceof ErrorDeApi && e.codigo === 'SIN_CONSENTIMIENTO_DE_SOPORTE') {
          setMensaje(e.message)
          setEstado('sinConsentimiento')
        } else {
          setMensaje(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar el resumen contable.')
          setEstado('error')
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [despacho.id])

  return (
    <div className="modal" {...telon(() => onCerrar())}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(720px, 94vw)' }}
      >
        <h2 id={idTitulo}>Resumen contable de {despacho.nombreComercial}</h2>
        <p className="sutil">
          Solo visible mientras el despacho tenga un consentimiento de soporte vigente (RF-107, §6.4).
        </p>

        {estado === 'cargando' && <p className="vacio">Cargando…</p>}
        {(estado === 'sinConsentimiento' || estado === 'error') && (
          <p className="error" role="alert">{mensaje}</p>
        )}

        {estado === 'ok' && (
          <div className="tabla-envoltura">
            <table className="tabla">
              <thead>
                <tr>
                  <th>RFC</th>
                  <th>Razón social</th>
                  <th>Periodos abiertos</th>
                  <th>CFDI sin contabilizar</th>
                  <th>Semáforo</th>
                </tr>
              </thead>
              <tbody>
                {resumen.map((c) => (
                  <tr key={c.clienteId}>
                    <td className="mono">{c.rfc}</td>
                    <td>{c.razonSocial}</td>
                    <td>{c.periodosAbiertos}</td>
                    <td>{c.cfdiSinContabilizar}</td>
                    <td>{c.semaforo}</td>
                  </tr>
                ))}
                {resumen.length === 0 && (
                  <tr>
                    <td colSpan={5} className="vacio">Sin clientes activos.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="acciones">
          <button type="button" onClick={onCerrar}>Cerrar</button>
        </div>
      </div>
    </div>
  )
}
